import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import NumberFormat from 'react-number-format';
import { makeStyles, withStyles, lighten } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Rating from '@material-ui/lab/Rating';
import { Button, IconButton, Dialog, DialogContent } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
import CheckIcon from '@material-ui/icons/Check';
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import './MCTQuoteCard.scss';
import SvgIcon from '../../../Helpers/Svg/SvgIcon';
import AmericoQuoteCard from './AmericoQuoteCard';
import { QuoteProductID } from '../QuoteProductID';

const StyledRating = withStyles((theme) => ({
  iconFilled: {
    color: theme.palette.primary.main,
  },
}))(Rating);

const TooltipColor = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
  ribbonColor: {
    color: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}26`,
  },
  quoteCardColor: {
    color: theme.palette.primary.main,
  },
  priceColor: {
    color: theme.palette.primary.main,
    fontSize: "34px",
    fontWeight: "bold",
    letterSpacing: "-0.16px",
    '&::before': {
      color: theme.palette.primary.main,
      content: '"$"',
      fontSize: "12px",
      verticalAlign: "super",
    }
  },
  priceText: {
    color: theme.palette.primary.main,
    fontSize: "12px"
  },
  lightBackground: {
    backgroundColor: lighten(theme.palette.primary.main, 0.98),
  },
  featuredBackground: {
    backgroundColor: theme.palette.primary.main,
  },
  featuredText: {
    color: "#ffffff",
  },
  featuredButton: {
    backgroundColor: "#ffffff",
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: lighten(theme.palette.primary.main, 0.9),
    }
  },
}));

export default function MCTQuoteCard(props) {
  const [isProductDetailsDialogOpen, setIsProductDetailsDialogOpen] = useState(false);
  const classes = useStyles();

  const getBadge = () => {
    // if this product has more than 1 badge, display the badge that corresponds to the sorting if available
    var bestCount = [props.isBestPrice, props.isFastest, props.isMostOptions].filter((f) => f === true).length;

    if (bestCount > 1) {
      if (props.isBestPrice && props.sortBy === 'price') {
        return (
          <Fragment>
            Best Price
          </Fragment>
        );
      }

      if (props.isFastest && props.sortBy === 'speed') {
        return (
          <Fragment>
            Fastest
          </Fragment>
        );
      }

      if (props.isMostOptions && props.sortBy === 'options') {
        return (
          <Fragment>
            Most Options
          </Fragment>
        );
      }

      if (props.isFeaturedCarrier) {
        return (
          <Fragment>
            Featured
          </Fragment>
        );
      }
    }

    // now either it only has 1 badge, or it doesn't have the badge being sorted by, so pick one

    if (props.isFeaturedCarrier && props.isBestPrice) {
      return (
        <Fragment>
          Featured
        </Fragment>
      );
    }

    if (props.isFeaturedCarrier && props.isFastest) {
      return (
        <Fragment>
          Featured
        </Fragment>
      );
    }

    if (props.isFeaturedCarrier && props.isMostOptions) {
      return (
        <Fragment>
          Featured
        </Fragment>
      );
    }

    if (props.isBestPrice) {
      return (
        <Fragment>
          Best Price
        </Fragment>
      );
    }

    if (props.isFastest) {
      return (
        <Fragment>
          Fastest
        </Fragment>
      );
    }

    if (props.isFeaturedCarrier) {
      return (
        <Fragment>
          Featured
        </Fragment>
      );
    }

    return (
      <Fragment>
        Most Options
      </Fragment>
    );
  };

  const productDetailsButton = (productName, isMobile) => {
    return (
      <Row>
        <Col>
          <Button className={`small-text ${isMobile ? "mobile-details-button" : ""} ${props.isFeaturedCarrier ? classes.featuredText : ""}`} color="primary" onClick={() => setIsProductDetailsDialogOpen(true)} aria-describedby={productName}>{!isMobile && `PRODUCT`} DETAILS</Button>
        </Col>
      </Row>
    );
  }

  const isHavenLifeSIProduct = props.quote && props.quote.productID === QuoteProductID.HAVEN_SI_PRODUCT_ID;
  const isHavenTermProduct = props.quote && props.quote.productID === QuoteProductID.HAVEN_TERM_PRODUCT_ID;

  return (
    <div className="mct-card-wrapper">
      {(props.isBestPrice || props.isFastest || props.isMostOptions || props.isFeaturedCarrier) && <div className={`ribbon ${classes.ribbonColor}`}>{getBadge()}</div>}
      <Card variant="outlined" className="highlight-quote-card">
        <CardContent className={`card-content ${props.isFeaturedCarrier ? classes.featuredBackground : ""} ${props.isFeaturedCarrier ? classes.featuredText : ""}`}>
          <Row className="align-items-center">
            <Col lg={3} className={`carrier-branding-container d-flex flex-column justify-content-center align-items-center ${classes.lightBackground}`}>
              <img className="carrier-logo-lg" src={props.logoUrl} alt={`${props.carrierName}`} />
              <p className={`tiny-text ${classes.quoteCardColor}`}>{props.rating} Rating</p>
              {productDetailsButton(props.productName, true)}
            </Col>
            <Col lg={5} className="pl-0 stat-container flex-column justify-content-center">
              <Stats
                additionalOptionsStars={props.additionalOptionsStars}
                underwritingTimeTypical={props.underwritingTimeTypical}
                requiresMedicalExam={props.requiresMedicalExam}
                rating={props.rating}
                monthlyRate={props.monthlyRate}
                isDecreasingTerm={props.isDecreasingTerm}
                showTermStruct={props.showTermStruct}
              />
              {productDetailsButton(props.productName)}
            </Col>
            <Col lg={4} className="card-item-group">
              <Row>
                <Col lg={6} className="d-flex flex-column justify-content-center pl-0">
                  <p className="price-container">
                    <NumberFormat
                      className={`${classes.priceColor} ${props.isFeaturedCarrier ? classes.featuredText : ""}`}
                      value={props.monthlyRate}
                      displayType={'text'}
                      thousandSeparator={true}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                    <span className={`quote-amount-mo ${classes.priceText} ${props.isFeaturedCarrier ? classes.featuredText : ""}`}> /mo</span>
                  </p>
                </Col>
                <Col lg={6} className="select-container d-flex flex-column">
                  <Button className={`${props.isFeaturedCarrier ? classes.featuredButton : ""}`} color="primary" variant="contained" onClick={props.onClick} aria-describedby={props.productName}>
                    Select
                  </Button>
                </Col>
              </Row>

            </Col>
          </Row>
        </CardContent>
      </Card>

      {props.carrierName !== "Americo" ?
        < ProductDetailsDialog
          isOpen={isProductDetailsDialogOpen}
          productDetails={props.productDetails}
          quoteCardProps={props}
          onClose={() => setIsProductDetailsDialogOpen(false)}
          isHavenTermProduct={isHavenTermProduct}
          isHavenLifeSIProduct={isHavenLifeSIProduct}
        />
        :
        <AmericoQuoteCard
          onNext={props.onNext}
          logoUrl={props.logoUrl}
          monthlyRate={props.monthlyRate}
          show={isProductDetailsDialogOpen}
          onHide={() => setIsProductDetailsDialogOpen(false)}
        />
      }
    </div >
  );
}

const ProductDetailsRatingCard = (props) => {
  const classes = useStyles();
  return (
    <Card className="rating-card">
      <div className="rating-card-head">
        <img src={props.logo} alt={props.rater} className="img-fluid" />
      </div>
      <p className="rater">{props.rater}</p>
      <p className={`rating ${classes.quoteCardColor}`}>{props.rating}</p>
    </Card>
  );
};

export const ProductDetailsDialog = (props) => {

  const [tooltip, setTooltip] = useState(false);
  const productTermLength = props.quoteCardProps.val || props.quoteCardProps.termLength;

  const handleTooltipClose = () => {
    setTooltip(false);
  };

  const handleTooltipOpen = () => {
    setTooltip(true);
  };
  const classes = useStyles();
  const hasRating =
    props.quoteCardProps.ratingAMBest ||
    props.quoteCardProps.ratingSP ||
    props.quoteCardProps.ratingMoodys ||
    props.quoteCardProps.ratingFitch ||
    props.quoteCardProps.ratingComdex;

  return (
    <Dialog className="product-details-dialog" open={props.isOpen} scroll="body" fullWidth={true} maxWidth="md" aria-labelledby="product-details-dialog">
      <DialogContent className="product-details-dialog-content">
        <IconButton aria-label="close" onClick={props.onClose} className="close-button">
          <CloseIcon />
        </IconButton>
        <img className="carrier-logo-lg" src={props.quoteCardProps.logoUrl} alt={`${props.quoteCardProps.carrierName}`} />

        <p className={`mt-3 textRating ${classes.quoteCardColor}`}>
          {props.quoteCardProps.rating}
          <span className="ml-1">Rating</span>
        </p>

        <div className="product mb-1">
          <span>{props.quoteCardProps.productName}</span>
          <span> {props.isHavenLifeSIProduct ? productTermLength : `- ${productTermLength} Year Term`}</span>
        </div>

        <Row>
          <Col xs="3" md="9">
            <Stats
              underwritingTimeTypical={props.quoteCardProps.underwritingTimeTypical}
              additionalOptionsStars={props.quoteCardProps.additionalOptionsStars}
              requiresMedicalExam={props.quoteCardProps.requiresMedicalExam}
              monthlyRate={props.quoteCardProps.monthlyRate}
              rating={props.quoteCardProps.rating}
              isDecreasingTerm={props.isDecreasingTerm}
            />
          </Col>

          <Col xs="6" md="3">
            {!props.hideSelect && <Button className={`w-100 apply ${props.quoteCardProps.hideSelect ? "d-none" : ""}`} color="primary" variant="contained" onClick={props.quoteCardProps.onClick}>
              Select
            </Button>}
          </Col>
        </Row>

        <div className="product-details-info">{props.productDetails}</div>

        <div className="additional-options mt-4">
          <strong>
            <h2><b>Additional Options</b></h2>
          </strong>
          <Row>
            <Col md="3" className="mt-2 ml-2">
              <p className="option-title ">
                <b>Convertibility</b>
              </p>
              {props.quoteCardProps.hasPermConversion ? (
                <p className="price-free mt-2">
                  <SvgIcon className="mr-1" iconName="select" />
                  <b>This product is convertible.</b>
                </p>
              ) : (
                <p className="price-red mt-2">
                  <SvgIcon className="mr-1" iconName="notConvertible" />
                  <b>This product is not convertible.</b>
                </p>
              )}
            </Col>
            {props.quoteCardProps.hasPermConversion && (
              <Col className="mt-2 ml-2">
                <ul className="grey-text" dangerouslySetInnerHTML={{ __html: props.quoteCardProps.convertibility }}></ul>
              </Col>
            )}
          </Row>
          {(props.quoteCardProps.availableRiders && props.quoteCardProps.availableRiders.length > 0) && (
            <strong className="mt-4 ml-2">
              <b>Available Policy Riders</b>
              <Tooltip className="toolInfo ml-2" title="Riders add benefits to or change the term of an insurance policy.">
                <TooltipColor role="tooltip" onClose={handleTooltipClose} open={tooltip} leaveTouchDelay={5000}>
                  <button id="riders" role="tooltip" aria-label="more about riders" className="quote-tooltip" onMouseOver={handleTooltipOpen} onClick={handleTooltipOpen} tabIndex="0" aria-disabled="true" aria-describedby="riders">
                    <InfoIcon color="action"></InfoIcon>
                  </button>
                </TooltipColor>
              </Tooltip>
            </strong>
          )}

          {props.quoteCardProps.availableRiders &&
            props.quoteCardProps.availableRiders.length > 0 &&
            props.quoteCardProps.availableRiders.map((r, index) => {
              return (
                <Row key={index}>
                  <Col md="3">
                    <p className="option-title mt-4 ml-3">{r.rider}</p>
                    {r.additionalCost && (
                      <p className="price-extra mt-2 ml-3">
                        <MonetizationOnOutlinedIcon /> <b className="addtional">Additional Cost</b>
                      </p>
                    )}
                    {!r.additionalCost && (
                      <p className="mt-2 ml-3 moneyIcon">
                        <MonetizationOnOutlinedIcon /> <b className={`noAddtional ${classes.quoteCardColor}`}>No Additional Cost</b>
                      </p>
                    )}
                  </Col>
                  <Col>
                    <p dangerouslySetInnerHTML={{ __html: r.description }} className="grey-text mt-4 ml-2"></p>
                  </Col>
                </Row>
              );
            })}
        </div>

        {hasRating && (
          <div className="ratings">
            <div>
              <h2><strong>Insurance Carrier Ratings</strong></h2>
            </div>
            <ul className="d-flex rating-cards">
              <li>
                {props.quoteCardProps.ratingAMBest && (
                  <ProductDetailsRatingCard logo="/logos/ratings/ambest.jpg" rater="AM Best" rating={props.quoteCardProps.ratingAMBest} />
                )}
              </li>
              <li>
                {props.quoteCardProps.ratingSP && <ProductDetailsRatingCard logo="/logos/ratings/sp.jpg" rater="S&amp;P Global" rating={props.quoteCardProps.ratingSP} />}
              </li>
              <li>
                {props.quoteCardProps.ratingMoodys && (
                  <ProductDetailsRatingCard logo="/logos/ratings/moodys.jpg" rater="Moody's" rating={props.quoteCardProps.ratingMoodys} />
                )}
              </li>
              <li>
                {props.quoteCardProps.ratingFitch && <ProductDetailsRatingCard logo="/logos/ratings/fitch.jpg" rater="Fitch" rating={props.quoteCardProps.ratingFitch} />}
              </li>
              <li>
                {props.quoteCardProps.ratingComdex && (
                  <ProductDetailsRatingCard logo="/logos/ratings/comdex.jpg" rater="Comdex" rating={props.quoteCardProps.ratingComdex} />
                )}
              </li>
            </ul>
          </div>
        )}
        {props.isHavenTermProduct || props.isHavenLifeSIProduct ? (
          <div className="mt-4">
            <h3 className=" text-center">Disclosure</h3>
            <p className="grey-text">{props.quoteCardProps.disclosure || props.quoteCardProps.disclosureDetail}</p>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const Stats = (props) => {
  return (
    <div className="product-details-stats">
      <Row>
        <Col className="d-flex flex-column justify-content-center pr-2">
          <p className="stat-title">Approval Time</p>
          <p className="stat-text">{props.underwritingTimeTypical}</p>
        </Col>

        <Col className="d-flex flex-column justify-content-center pl-0 pr-2">
          <p className="stat-title">Medical Exam</p>
          <p className="stat-text">{props.requiresMedicalExam}</p>
        </Col>

        {props.showTermStruct && (
          <Col className="d-flex flex-column justify-content-center pl-0 pr-2">
            <p className="stat-title">Term Structure</p>
            <p className="stat-text">{props.isDecreasingTerm === null ? "Level" : "Decreasing"}</p>
          </Col>
        )}
      </Row>
    </div>
  );
};

MCTQuoteCard.propTypes = {
  carrierName: PropTypes.string.isRequired,
  monthlyRate: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  productDetails: PropTypes.object.isRequired,
  isFeaturedCarrier: PropTypes.bool.isRequired,
};



// WEBPACK FOOTER //
// ./src/components/Quoters/Common/Cards/MCTQuoteCard.js